import Currency from 'components/currency/currency';
import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  CardBody,
  Col,
  Row,
  UncontrolledAccordion,
} from 'reactstrap';
import messages from '../messages';
import TextInput from 'components/text-input';
import ISourcingFeesForm from '../use-sourcing-fees-form';
import ListSkeleton from './skeleton';

const BodySection: FC<Props> = ({
  params,
  isLoading,
  handleChangeGlobalComment,
}) => {
  const {t} = useTranslation();

  const {invoices, sourcing_fees_global_comment} = params;

  return (
    <CardBody>
      <div className="mb-4">
        <TextInput
          type="textarea"
          value={sourcing_fees_global_comment}
          label={t(...messages.comment())}
          onChange={val => handleChangeGlobalComment(val)}
        />
      </div>
      <hr />
      {isLoading ? (
        <ListSkeleton />
      ) : (
        <>
          {invoices.length > 0 ? (
            <UncontrolledAccordion>
              {invoices.map((item, x) => (
                <AccordionItem key={x}>
                  <AccordionHeader targetId={x.toString()}>
                    <Row className="w-100">
                      <Col lg={3}>
                        <h6>{t(...messages.invoiceNumber())}</h6>
                        <p>
                          <Link
                            target="_BLANK"
                            to={`/dashboard/invoices/${item.id}/details`}
                          >
                            {item.document_ref}
                          </Link>
                        </p>
                      </Col>
                      <Col lg={3}>
                        <h6>{t(...messages.billingDate())}</h6>
                        <p>
                          {new Date(item.billing_date).toLocaleDateString()}
                        </p>
                      </Col>
                      <Col lg={3}>
                        <div className="d-flex gap-4">
                          <div>
                            <h6>{t(...messages.totalHT())}</h6>
                            <p>
                              <strong>
                                <Currency value={item.total_ht} />
                              </strong>
                            </p>
                          </div>
                          <div>
                            <h6>{t(...messages.commission())}</h6>
                            <p>
                              <strong>
                                <Currency value={item.total_commission} />
                              </strong>
                            </p>
                          </div>
                        </div>
                      </Col>
                      <Col lg={3}>
                        <h6>{t(...messages.dueDate())}</h6>
                        <p>{item.payment_summary.last_payment_date}</p>
                      </Col>
                    </Row>
                    <Badge
                      color={
                        !item.billing_date &&
                        !item.payment_summary?.last_payment_date
                          ? 'black'
                          : item.billing_date &&
                            !item.payment_summary?.last_payment_date
                          ? 'danger'
                          : 'success'
                      }
                      className="position-absolute top-0 end-0 mt-3 me-2"
                    >
                      {!item.billing_date &&
                      !item.payment_summary?.last_payment_date
                        ? t(...messages.toInvoice())
                        : item.billing_date &&
                          !item.payment_summary?.last_payment_date
                        ? t(...messages.pending())
                        : t(...messages.paid())}
                    </Badge>
                  </AccordionHeader>
                  <AccordionBody accordionId={x.toString()}>
                    <Row>
                      <Col lg={3}>
                        <h6>{t(...messages.products())}</h6>
                      </Col>
                      <Col lg={3}>
                        <h6>{t(...messages.quantity())}</h6>
                      </Col>
                      <Col lg={6} />
                    </Row>
                    {item.commercial_products.length > 0 ? (
                      <>
                        {item.commercial_products.map(
                          (commercial_product, y) => (
                            <div key={x + y}>
                              <Row>
                                <Col lg={3}>
                                  {commercial_product.commercial_product_type ===
                                  'PRODUCT' ? (
                                    <Link
                                      target="_BLANK"
                                      to={`/dashboard/products/${commercial_product.product_variant?.product_id}/variants/${commercial_product.product_variant?.id}/general`}
                                    >
                                      {commercial_product.custom_name}
                                    </Link>
                                  ) : (
                                    <p>{commercial_product.custom_name}</p>
                                  )}
                                </Col>
                                <Col lg={3}>
                                  <p>{commercial_product.quantity}</p>
                                </Col>
                                <Col lg={6}>
                                  <div className="d-flex gap-4">
                                    <div className="align-self-start">
                                      <Currency
                                        value={Number(
                                          commercial_product.total_price_ht
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <Currency
                                        value={Number(
                                          commercial_product.commission
                                        )}
                                      />
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                              <hr
                                style={{
                                  display:
                                    y < item.commercial_products.length - 1
                                      ? 'block'
                                      : 'none',
                                }}
                              />
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <p className=" text-center">{t('common.noData')}</p>
                    )}
                  </AccordionBody>
                </AccordionItem>
              ))}
            </UncontrolledAccordion>
          ) : (
            <div className="d-flex justify-content-center py-4">
              <p>{t('common.noData')}</p>
            </div>
          )}
        </>
      )}
    </CardBody>
  );
};

type Props = ISourcingFeesForm;

export default BodySection;
